/**
 * API client for AmandaMD backend.
 *
 * Communicates with Lambda functions via API Gateway.
 */

function readConfiguredApiBase() {
  if (typeof globalThis !== 'undefined' && typeof globalThis.__AMANDAMD_API_URL__ === 'string') {
    return globalThis.__AMANDAMD_API_URL__;
  }

  if (
    typeof import.meta !== 'undefined' &&
    import.meta &&
    import.meta.env &&
    typeof import.meta.env.VITE_API_URL === 'string'
  ) {
    return import.meta.env.VITE_API_URL;
  }

  return '/api';
}

const API_BASE = readConfiguredApiBase();
const LOCALHOST_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
const ERROR_STACK_PATTERN = /(traceback|stack trace|exception:|file \".+\", line \d+|\n\s+at\s)/i;
const MAX_ERROR_MESSAGE_LENGTH = 240;
let securityInitialized = false;
let authTokenProvider = null;

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

function getRuntimeOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost';
}

function resolveRuntimeUrl(value) {
  return new URL(value, getRuntimeOrigin());
}

function isLocalhostHttp(url) {
  return url.protocol === 'http:' && LOCALHOST_HOSTS.has(url.hostname);
}

function isSecureTransport(url) {
  return url.protocol === 'https:' || isLocalhostHttp(url);
}

function apiPathPrefix(pathname) {
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

function isApiRequestUrl(requestUrl, apiBaseUrl) {
  if (requestUrl.origin !== apiBaseUrl.origin) {
    return false;
  }
  if (requestUrl.pathname === apiBaseUrl.pathname) {
    return true;
  }
  return requestUrl.pathname.startsWith(apiPathPrefix(apiBaseUrl.pathname));
}

function getApiBaseUrl() {
  try {
    return resolveRuntimeUrl(API_BASE);
  } catch {
    throw new ApiError('Invalid API configuration.', 0, { code: 'invalid_api_base' });
  }
}

function assertSecureApiBase() {
  const apiBaseUrl = getApiBaseUrl();
  if (!isSecureTransport(apiBaseUrl)) {
    throw new ApiError(
      'Blocked insecure API configuration. Use HTTPS, or localhost HTTP for development.',
      0,
      { code: 'insecure_api_base' }
    );
  }
}

function extractRequestUrl(input) {
  if (typeof input === 'string') {
    return input;
  }
  if (input instanceof URL) {
    return input.toString();
  }
  if (input && typeof input.url === 'string') {
    return input.url;
  }
  return null;
}

function defaultMessageForStatus(status) {
  if (status === 400) return 'Invalid request.';
  if (status === 401) return 'Authentication required.';
  if (status === 403) return 'Access denied.';
  if (status === 404) return 'Requested resource was not found.';
  if (status === 409) return 'Request conflict. Please refresh and retry.';
  if (status === 429) return 'Too many requests. Please wait and retry.';
  if (status >= 500) return 'Server error. Please try again.';
  return 'Request failed.';
}

function sanitizeErrorMessage(rawMessage, status) {
  const fallback = defaultMessageForStatus(status);
  if (typeof rawMessage !== 'string') {
    return fallback;
  }

  const trimmed = rawMessage.trim();
  if (!trimmed || status >= 500 || ERROR_STACK_PATTERN.test(trimmed)) {
    return fallback;
  }

  return trimmed.replace(/\s+/g, ' ').slice(0, MAX_ERROR_MESSAGE_LENGTH);
}

async function parseResponseBody(response) {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  const text = await response.text();
  return text ? { error: text } : {};
}

export function initializeApiSecurity() {
  if (securityInitialized) {
    return;
  }

  assertSecureApiBase();

  if (typeof globalThis.fetch === 'function') {
    const apiBaseUrl = getApiBaseUrl();
    const nativeFetch = globalThis.fetch.bind(globalThis);

    globalThis.fetch = async (input, init) => {
      const rawUrl = extractRequestUrl(input);
      if (rawUrl) {
        const resolvedUrl = resolveRuntimeUrl(rawUrl);
        if (isApiRequestUrl(resolvedUrl, apiBaseUrl) && !isSecureTransport(resolvedUrl)) {
          throw new ApiError(
            'Blocked insecure API request. Use HTTPS, or localhost HTTP for development.',
            0,
            { code: 'insecure_transport_blocked' }
          );
        }
      }

      return nativeFetch(input, init);
    };
  }

  securityInitialized = true;
}

export function resetApiSecurityForTests() {
  securityInitialized = false;
  authTokenProvider = null;
}

export function setAuthTokenProvider(provider) {
  if (provider !== null && typeof provider !== 'function') {
    throw new ApiError('Invalid auth token provider.', 0, { code: 'invalid_token_provider' });
  }
  authTokenProvider = provider;
}

async function request(endpoint, options = {}) {
  initializeApiSecurity();

  const url = `${API_BASE}${endpoint}`;

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    if (authTokenProvider) {
      const token = await authTokenProvider();
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, config);
    const data = await parseResponseBody(response);

    if (!response.ok) {
      const rawMessage = data?.error || data?.message;
      throw new ApiError(
        sanitizeErrorMessage(rawMessage, response.status),
        response.status,
        { code: data?.code || null }
      );
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      sanitizeErrorMessage(error?.message, 0),
      0,
      { code: 'network_error' }
    );
  }
}

export const apiClient = {
  // Client operations
  async getClients(status = 'active') {
    return request(`/clients?status=${status}`);
  },

  async getClient(clientId) {
    return request(`/clients/${clientId}`);
  },

  async createClient(data) {
    return request('/clients', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateClientKeyBundle(clientId, keyBundle) {
    return request(`/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify({ key_bundle: keyBundle }),
    });
  },

  async archiveClient(clientId) {
    return request(`/clients/${clientId}`, {
      method: 'DELETE',
    });
  },

  // Upload operations
  async requestUpload(clientId, files, mode = 'batch') {
    return request('/upload', {
      method: 'POST',
      body: JSON.stringify({
        client_id: clientId,
        files,
        mode,
      }),
    });
  },

  async unlockProcessing(jobId, clientId, dekB64, ttlSeconds = 600) {
    return request('/upload', {
      method: 'POST',
      body: JSON.stringify({
        action: 'unlock_processing',
        job_id: jobId,
        client_id: clientId,
        dek_b64: dekB64,
        ttl_seconds: ttlSeconds,
      }),
    });
  },

  async revokeProcessingUnlock(jobId) {
    return request('/upload', {
      method: 'POST',
      body: JSON.stringify({
        action: 'revoke_processing_unlock',
        job_id: jobId,
      }),
    });
  },

  // Status operations
  async getJobStatus(jobId, includeFiles = false) {
    const endpoint = includeFiles
      ? `/status/${jobId}/files`
      : `/status/${jobId}`;
    return request(endpoint);
  },

  // File operations
  async listFiles(prefix) {
    if (typeof prefix !== 'string' || !prefix.trim()) {
      throw new ApiError('Invalid file prefix.', 400, { code: 'invalid_prefix' });
    }
    return request(`/files?prefix=${encodeURIComponent(prefix)}`);
  },

  async getDownloadUrl(key) {
    if (typeof key !== 'string' || !key.trim()) {
      throw new ApiError('Invalid file key.', 400, { code: 'invalid_key' });
    }
    const response = await request(`/files/download?key=${encodeURIComponent(key)}`);
    return response.url;
  },

  // Warm-up operations
  async getWorkerStatus() {
    return request('/warmup');
  },

  async startWorker() {
    return request('/warmup', {
      method: 'POST',
    });
  },
};

export const __apiSecurityInternals = {
  getApiBaseUrl,
  isSecureTransport,
  sanitizeErrorMessage,
  parseResponseBody,
};

export default apiClient;
